// Build the GV `/rest/trims/{tid}` job list for a given byVehicle map and
// (optional) MSS showcase mapping. Pure — no fetches, no logging, no throws.
//
// Used by:
//   - SSR (src/server/store.js) to fan out the first-N vehicle cohort
//   - POST /vehicle-images (src/server/endpoints.js) to validate/echo the
//     job list against the same logic
//   - Client hook (src/hooks/useDeferredVehicleImages.js) to compute the
//     deferred-cohort job list from ssrState.incentives + showcaseMappings
//
// Each job is { gvId, trim, tid } — one default trim per vehicle, plus any
// MSS-named (model, trim) extras the showcase rules supply, deduped by
// (gvId, tid). Preserves byVehicle iteration order — callers can take the
// first N vehicles' jobs by slicing on unique gvId order.

// Collect every owned (trim, tid) pair on a vehicle. "Owned" = the tid is in
// the vehicle's globalVehicleTrimIds set (so trim entries shared across
// incentives but pointing at a different gvId's tid get filtered out). De-dupes
// by tid so multi-incentive trims appear once. Preserves discovery order.
const collectOwnedTrimPairs = (vehicle) => {
	const ownedTids = new Set(vehicle?.globalVehicleTrimIds || []);
	if (ownedTids.size === 0) return [];
	const seen = new Set();
	const pairs = [];
	(vehicle.incentives || []).forEach((incentive) => {
		(incentive.trims || []).forEach((entry) => {
			if (!entry || !entry.trim) return;
			const tid = (entry.globalVehicleTrimIds || []).find((id) =>
				ownedTids.has(id)
			);
			if (!tid || seen.has(tid)) return;
			seen.add(tid);
			pairs.push({ trim: entry.trim, tid });
		});
	});
	return pairs;
};

// Walk MSS rules and surface (trim, tid) extras for a single vehicle: for each
// rule whose matches[].value matches the vehicle model and whose matches[].trim
// is set, find the owned tid for that trim name. Returns [] when no MSS rules
// or no model match. The caller dedupes against the default trim job by tid.
const collectMssTrimsForVehicle = (vehicle, mssShowcase) => {
	if (
		!vehicle ||
		!vehicle.model ||
		!mssShowcase ||
		!Array.isArray(mssShowcase.mappingRules)
	) {
		return [];
	}
	const vehicleModel = String(vehicle.model).toLowerCase();
	const ownedPairs = collectOwnedTrimPairs(vehicle);
	const trimToTid = new Map(ownedPairs.map(({ trim, tid }) => [trim, tid]));

	const extras = [];
	mssShowcase.mappingRules.forEach((rule) => {
		(rule.matches || []).forEach((match) => {
			if (!match || !match.trim || !match.value) return;
			if (String(match.value).toLowerCase() !== vehicleModel) return;
			const tid = trimToTid.get(match.trim);
			if (tid) {
				extras.push({ trim: match.trim, tid });
			}
		});
	});
	return extras;
};

const buildTrimJobs = (byVehicle, mssShowcase) => {
	const jobs = [];
	const seen = new Set();
	const push = (gvId, trim, tid) => {
		const key = `${gvId}|${tid}`;
		if (seen.has(key)) return;
		seen.add(key);
		jobs.push({ gvId, trim, tid });
	};

	Object.entries(byVehicle || {}).forEach(([gvId, vehicle]) => {
		const ownedPairs = collectOwnedTrimPairs(vehicle);
		if (ownedPairs.length > 0) {
			const [first] = ownedPairs;
			push(gvId, first.trim, first.tid);
		}
		collectMssTrimsForVehicle(vehicle, mssShowcase).forEach(
			({ trim, tid }) => push(gvId, trim, tid)
		);
	});

	return jobs;
};

export { collectOwnedTrimPairs, collectMssTrimsForVehicle };
export default buildTrimJobs;
